import { lazy, Suspense, useEffect, useRef, useState } from 'react'
import {
  ArrowRight,
  CheckCircle2,
  ChevronDown,
  Clock,
  Eye,
  Globe2,
  Link2,
  Lock,
  Menu,
  MessageCircle,
  Package,
  PenLine,
  Percent,
  RotateCcw,
  Send,
  Shield,
  ShoppingCart,
  Sparkles,
  Trash2,
  X,
  Zap,
} from 'lucide-react'
import { MdOutlineChatBubbleOutline, MdOutlineEmail } from 'react-icons/md'
import { FaLinkedin } from 'react-icons/fa6'
import { SiFacebook, SiInstagram, SiMessenger, SiMeta, SiShopify, SiWhatsapp, SiWoocommerce } from 'react-icons/si'
import { demoRequestsApi } from '../../api/demoRequests'
import { blogApi } from '../../api/blog'
import { languages, usePublicI18n, type Language } from '../../publicI18n'
import { FlagIcon } from '../../components/ui/FlagIcon'
import { resolveCoverUrl } from '../../utils/seo'
import { useLandingCopy } from './landingLocale'

const navLogo = '/dotkesk-orbit-logo-40.png'
const appBaseUrl = (import.meta.env.VITE_APP_URL as string | undefined)?.replace(/\/$/, '')
const appLoginUrl = appBaseUrl ? `${appBaseUrl}/login` : '/login'
const landingWebChatChannelId = '3dcce450-576d-4a8a-a9d1-62c6cf1c1a01'
const landingWebChatApiBase = (import.meta.env.VITE_API_URL as string | undefined)?.replace(/\/$/, '') || 'http://localhost:5135'

const EASE = 'ease-[cubic-bezier(0.25,0.1,0.25,1)]'
const HeroShader = lazy(() => import('./HeroShader'))
function HeroChannels() {
  const copy = useLandingCopy()
  const channels = [
    { label: 'Instagram', icon: <SiInstagram size={14} /> },
    { label: 'Facebook', icon: <SiFacebook size={14} /> },
    { label: 'WhatsApp', icon: <SiWhatsapp size={15} /> },
    { label: 'Meta', icon: <SiMeta size={16} /> },
    { label: 'Email', icon: <MdOutlineEmail size={16} /> },
    { label: 'Web Chat', icon: <MdOutlineChatBubbleOutline size={15} /> },
  ]
  return (
    <div role="img" className="inline-flex items-center bg-white rounded-full p-2 shadow-[0_2px_10px_rgba(0,0,0,0.08)] self-start" aria-label={copy.channelsBadge}>
      <div className="flex -space-x-1.5">
        {channels.map((channel) => (
          <span key={channel.label} title={channel.label} aria-hidden="true" className="w-7 h-7 rounded-full bg-[#F1F7F1] border-2 border-white text-[#16A34A] flex items-center justify-center">
            {channel.icon}
          </span>
        ))}
      </div>
    </div>
  )
}

function HeroStores() {
  const copy = useLandingCopy()
  const stores = [
    { label: 'ikas', icon: <Zap size={14} /> },
    { label: 'Shopify', icon: <SiShopify size={14} /> },
    { label: 'WooCommerce', icon: <SiWoocommerce size={16} /> },
    { label: 'Custom e-commerce', icon: <Globe2 size={14} /> },
  ]
  return (
    <div role="img" className="inline-flex items-center rounded-full bg-white p-2 shadow-[0_2px_10px_rgba(0,0,0,0.08)] self-start" aria-label={copy.storesBadge}>
      <div className="flex -space-x-1.5">
        {stores.map((store) => (
          <span key={store.label} title={store.label} aria-hidden="true" className="flex h-7 w-7 items-center justify-center rounded-full border-2 border-white bg-[#F1F7F1] text-[#16A34A]">
            {store.icon}
          </span>
        ))}
      </div>
    </div>
  )
}

export function LandingLanguageSwitcher({ mobile = false }: { mobile?: boolean }) {
  const { language, setLanguage } = usePublicI18n()
  const [open, setOpen] = useState(false)
  const ref = useRef<HTMLDivElement>(null)
  const current = languages.find((item) => item.code === language) ?? languages[0]

  useEffect(() => {
    const close = (event: MouseEvent) => {
      if (ref.current && !ref.current.contains(event.target as Node)) setOpen(false)
    }
    document.addEventListener('mousedown', close)
    return () => document.removeEventListener('mousedown', close)
  }, [])

  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        onClick={() => setOpen((value) => !value)}
        aria-expanded={open}
        aria-label="Language"
        className={`flex items-center gap-2 rounded-full border border-gray-200 bg-white text-[12px] font-semibold text-gray-800 shadow-[0_2px_10px_rgba(0,0,0,0.06)] transition hover:border-gray-300 ${mobile ? 'px-3 py-2' : 'px-3 py-2'}`}
      >
        <span className="overflow-hidden rounded-full border border-gray-100"><FlagIcon code={current.code} size={14} /></span>
        <span>{current.shortLabel}</span>
        <ChevronDown size={12} className={`text-gray-400 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>
      {open && (
        <div className={`absolute z-[70] mt-2 w-44 overflow-hidden rounded-2xl border border-gray-200 bg-white p-1.5 shadow-[0_18px_50px_rgba(15,23,42,0.16)] ${mobile ? 'left-0' : 'right-0'}`}>
          {languages.map((item) => (
            <button
              type="button"
              key={item.code}
              onClick={() => { setLanguage(item.code as Language); setOpen(false) }}
              className={`flex w-full items-center gap-2.5 rounded-xl px-3 py-2.5 text-left text-[12px] transition ${item.code === language ? 'bg-[#F1F7F1] font-semibold text-[#15803D]' : 'text-gray-700 hover:bg-gray-50'}`}
            >
              <FlagIcon code={item.code} size={15} /><span className="flex-1">{item.label}</span>
              {item.code === language && <CheckCircle2 size={13} />}
            </button>
          ))}
        </div>
      )}
    </div>
  )
}

/* ── Hover'da dikey kayan metin ── */
function TextRoll({ text, heightClass = 'h-[20px]' }: { text: string; heightClass?: string }) {
  return (
    <span className={`relative block overflow-hidden ${heightClass}`}>
      <span className={`flex flex-col transition-transform duration-500 ${EASE} group-hover:-translate-y-1/2`}>
        <span className={`flex items-center ${heightClass}`}>{text}</span>
        <span className={`flex items-center ${heightClass}`}>{text}</span>
      </span>
    </span>
  )
}

function useWebGPUSupported() {
  const [supported, setSupported] = useState(false)
  useEffect(() => {
    let cancelled = false
    let firstFrame = 0
    let secondFrame = 0

    const verify = async () => {
      try {
        // shaders/react, WebGL değil WebGPU/typegpu kullanıyor. API'nin yalnızca
        // mevcut olması yeterli değil; Lighthouse gibi GPU'suz ortamlarda
        // requestAdapter() null döner. Shader'ı ancak gerçek bir adaptör varsa yükle.
        const adapter = await navigator.gpu?.requestAdapter()
        if (!adapter || cancelled) return
        firstFrame = requestAnimationFrame(() => {
          secondFrame = requestAnimationFrame(() => setSupported(true))
        })
      } catch {
        // Statik hero gradient'i zaten güvenli fallback olarak görünür.
      }
    }
    void verify()

    return () => {
      cancelled = true
      cancelAnimationFrame(firstFrame)
      cancelAnimationFrame(secondFrame)
    }
  }, [])
  return supported
}

function useIstanbulTime() {
  const [time, setTime] = useState('')
  useEffect(() => {
    const fmt = new Intl.DateTimeFormat('en-GB', {
      timeZone: 'Europe/Istanbul',
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
    })
    const tick = () => setTime(fmt.format(new Date()))
    tick()
    const id = setInterval(tick, 1000)
    return () => clearInterval(id)
  }, [])
  return time
}

/* ─────────────────────────── HERO ─────────────────────────── */
function Hero() {
  const copy = useLandingCopy()
  const time = useIstanbulTime()
  const webgpuSupported = useWebGPUSupported()
  const [menuOpen, setMenuOpen] = useState(false)
  const navLinks = [
    { label: copy.navPlatform, href: '#platform' },
    { label: copy.navAgents, href: '#capabilities' },
    { label: copy.navIntegrations, href: '#channels' },
    { label: copy.navBlog, href: '/blog' },
    { label: copy.navFaq, href: '#faq' },
  ]

  return (
    <section
      className="relative h-screen flex flex-col overflow-hidden"
      style={{ background: 'radial-gradient(120% 100% at 50% 0%, #ffffff 0%, #daf2e0 100%)' }}
    >
      {webgpuSupported && (
        <Suspense fallback={null}><HeroShader /></Suspense>
      )}

      {/* Navigation */}
      <div className="fixed inset-x-0 top-0 z-[60] mx-auto w-full max-w-[1440px] p-2 sm:p-3">
        <nav className="flex items-center justify-between rounded-full border border-white/70 bg-white/95 p-[5px] shadow-[0_8px_30px_rgba(15,23,42,0.08)] backdrop-blur-xl">
          <div className="flex items-center gap-6">
            <div className="w-9 h-9 sm:w-10 sm:h-10 bg-gray-900 rounded-full flex items-center justify-center overflow-hidden">
              <img src={navLogo} alt="dotkesk logo" width="40" height="29" fetchPriority="high" className="w-full h-full object-contain" />
            </div>
            <div className="hidden md:flex items-center gap-6">
              {navLinks.map((link) => (
                <a
                  key={link.href}
                  href={link.href}
                  className="text-[14px] text-gray-900 hover:text-gray-500 transition-colors duration-300"
                >
                  {link.label}
                </a>
              ))}
            </div>
          </div>

          <div className="hidden md:flex items-center gap-5 pr-1">
            <span className="hidden xl:block text-[13px] text-gray-600">{copy.onboarding}</span>
            <span className="flex items-center gap-1.5 text-[13px] text-gray-600">
              <Clock size={14} />
              {time} {copy.timeSuffix}
            </span>
            <LandingLanguageSwitcher />
            <a href={appLoginUrl} className="group flex items-center gap-3 bg-gray-900 text-white text-[13px] font-medium rounded-full pl-5 pr-2 py-2">
              <TextRoll text={copy.signIn} />
              <span className="w-6 h-6 bg-white rounded-full flex items-center justify-center">
                <ArrowRight
                  size={12}
                  className={`text-gray-900 transition-transform duration-500 ${EASE} group-hover:-rotate-45`}
                />
              </span>
            </a>
          </div>

          {/* Mobile toggle */}
          <button
            onClick={() => setMenuOpen(!menuOpen)}
            className="md:hidden flex items-center gap-1.5 bg-gray-900 text-white text-[13px] font-medium rounded-full px-4 py-2.5 mr-0.5"
          >
            {menuOpen ? <X size={14} /> : <Menu size={14} />}
            {menuOpen ? copy.close : copy.menu}
          </button>
        </nav>
      </div>

      {/* Mobile menu overlay */}
      <div
        className={`fixed inset-0 z-[70] md:hidden transition-opacity duration-300 ${
          menuOpen ? 'opacity-100' : 'opacity-0 pointer-events-none'
        }`}
      >
        <div className="absolute inset-0 bg-black/60" onClick={() => setMenuOpen(false)} />
        <div
          className={`absolute bottom-0 inset-x-0 mx-3 mb-3 bg-white rounded-2xl p-6 transition-transform duration-500 ease-[cubic-bezier(0.32,0.72,0,1)] ${
            menuOpen ? 'translate-y-0' : 'translate-y-full'
          }`}
        >
          <div className="flex items-center justify-between mb-8">
            <span className="flex items-center gap-1.5 text-[13px] text-gray-600 border border-gray-200 rounded-full px-3 py-1.5">
              <Clock size={14} />
              {time} {copy.timeSuffix}
            </span>
            <LandingLanguageSwitcher mobile />
            <button
              onClick={() => setMenuOpen(false)}
              className="flex items-center gap-1.5 bg-gray-900 text-white text-[13px] font-medium rounded-full px-4 py-2"
            >
              <X size={14} /> {copy.close}
            </button>
          </div>
          <div className="flex flex-col gap-4 mb-10">
            {navLinks.map((link) => (
              <a
                key={link.href}
                href={link.href}
                onClick={() => setMenuOpen(false)}
                className="text-[28px] sm:text-[32px] font-medium text-gray-900 leading-none"
              >
                {link.label}
              </a>
            ))}
          </div>
          <a href="#demo-form" onClick={() => setMenuOpen(false)} className="group w-full flex items-center justify-between bg-[#16A34A] text-white text-[14px] font-medium rounded-full pl-6 pr-2 py-2">
            <TextRoll text={copy.freeDemo} />
            <span className="w-8 h-8 bg-white rounded-full flex items-center justify-center">
              <ArrowRight
                size={14}
                className={`text-[#16A34A] transition-transform duration-500 ${EASE} group-hover:-rotate-45`}
              />
            </span>
          </a>
        </div>
      </div>

      {/* Spacer */}
      <div className="flex-1" />

      {/* Hero content */}
      <div className="relative z-20 max-w-[1440px] w-full mx-auto px-5 sm:px-8 lg:px-12 pb-14 sm:pb-16 lg:pb-20">
        <p className="text-[13px] sm:text-[14px] text-gray-900 tracking-wide mb-5 sm:mb-8">dotkesk — AI Commerce OS</p>
        <h1 className="text-[clamp(1.75rem,7vw,4.2rem)] sm:text-[clamp(2.5rem,5vw,4.2rem)] font-medium leading-[1.08] tracking-[-0.03em] text-gray-900">
          {copy.heroLine1}
          <br className="hidden sm:block" />
          <span className="sm:hidden"> </span>
          {copy.heroLine2}
          <br className="hidden sm:block" />
          <span className="sm:hidden"> </span>
          {copy.heroLine3}
        </h1>

        <div className="mt-8 sm:mt-12 flex flex-col sm:flex-row sm:items-center gap-4 sm:gap-5">
          <a href="#demo-form" className="group inline-flex items-center justify-between sm:justify-start gap-4 bg-[#15803D] hover:bg-[#16A34A] text-white text-[13px] sm:text-[14px] font-medium rounded-full pl-5 sm:pl-6 pr-2 py-2 transition-colors self-start">
            <TextRoll text={copy.freeDemo} />
            <span className="w-7 h-7 sm:w-8 sm:h-8 bg-white rounded-full flex items-center justify-center">
              <ArrowRight
                size={14}
                className={`text-[#16A34A] transition-transform duration-500 ${EASE} group-hover:-rotate-45`}
              />
            </span>
          </a>

          <HeroChannels />
          <HeroStores />
        </div>
      </div>
    </section>
  )
}

/* ─────────────────────────── TRUST BAR ─────────────────────────── */
function TrustBar() {
  const copy = useLandingCopy()
  const badges = [
    { icon: <Shield size={13} />, label: copy.trustBadge1 },
    { icon: <Trash2 size={13} />, label: copy.trustBadge2 },
    { icon: <Lock size={13} />, label: copy.trustBadge3 },
  ]
  return (
    <section className="bg-white border-b border-gray-100 py-5 sm:py-6">
      <div className="max-w-[1440px] mx-auto px-5 sm:px-8 lg:px-12 flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-6">
        <span className="text-[11px] font-medium text-gray-600 uppercase tracking-wide whitespace-nowrap">{copy.trustBarLabel}</span>
        <div className="flex flex-wrap items-center gap-2.5">
          {badges.map((badge) => (
            <span key={badge.label} className="inline-flex items-center gap-1.5 rounded-full border border-gray-200 bg-gray-50 px-3 py-1.5 text-[11px] font-medium text-gray-700">
              <span className="text-[#16A34A]">{badge.icon}</span>
              {badge.label}
            </span>
          ))}
        </div>
      </div>
    </section>
  )
}

/* ── Mockup: Omnichannel inbox (açık tema) ── */
function InboxCard() {
  const copy = useLandingCopy()
  return (
    <div className="w-full aspect-[3/2] rounded-2xl bg-[#F7F7F5] border border-gray-200 p-4 sm:p-5 overflow-hidden">
      <div className="bg-white rounded-xl border border-gray-200 shadow-[0_2px_12px_rgba(0,0,0,0.05)] h-full p-4 flex flex-col text-left">
        <div className="flex items-center justify-between pb-3 border-b border-gray-100">
          <div className="flex items-center gap-2.5">
            <div className="w-8 h-8 rounded-full bg-gradient-to-br from-[#f2f047] to-[#1ed94f] flex items-center justify-center text-gray-900 text-[10px] font-bold">
              ŞS
            </div>
            <div>
              <div className="text-[12px] font-semibold text-gray-900">Şimal Su Erdoğan</div>
              <div className="text-[10px] text-gray-500 flex items-center gap-1">
                <span className="w-1.5 h-1.5 rounded-full bg-[#16A34A]" /> WhatsApp · {copy.online}
              </div>
            </div>
          </div>
          <div className="flex items-center gap-1.5">
            <span className="text-[9px] font-medium px-2 py-0.5 rounded-full bg-green-50 text-[#15803D] border border-green-200">VIP</span>
            <span className="text-[9px] font-medium px-2 py-0.5 rounded-full bg-gray-50 text-gray-600 border border-gray-200">Lead</span>
          </div>
        </div>
        <div className="flex-1 flex flex-col gap-2 pt-3 min-h-0">
          <div className="bg-gray-100 rounded-lg rounded-bl-sm px-3 py-1.5 text-[11px] text-gray-700 self-start max-w-[80%]">
            {copy.chatCustomer}
          </div>
          <div className="bg-green-50 border border-green-100 rounded-lg rounded-br-sm px-3 py-1.5 text-[11px] text-gray-800 self-end max-w-[80%]">
            {copy.chatAnswer}
          </div>
          <div className="mt-auto bg-white border border-green-200 rounded-lg p-2.5 shadow-[0_2px_8px_rgba(22,163,74,0.08)]">
            <div className="flex items-center gap-1 text-[9px] font-semibold text-[#15803D] mb-1">
              <Sparkles size={10} /> {copy.suggestedReply}
            </div>
            <p className="text-[10px] text-gray-500 leading-snug">
              “{copy.suggestedText}”
            </p>
            <div className="flex items-center justify-between mt-1.5">
              <span className="text-[9px] text-gray-600">
                {copy.assigned}: <span className="text-gray-600">Selin · Sales</span>
              </span>
              <span className="text-[9px] font-semibold bg-[#15803D] text-white px-2 py-0.5 rounded-full">{copy.useReply}</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

/* ── Mockup: Kanallar & mağaza senkronu ── */
function ChannelsCard() {
  const copy = useLandingCopy()
  const channels = [
    { icon: <SiWhatsapp size={16} />, name: 'WhatsApp' },
    { icon: <SiInstagram size={15} />, name: 'Instagram' },
    { icon: <Send size={16} />, name: 'Telegram' },
    { icon: <MdOutlineChatBubbleOutline size={17} />, name: 'Web Chat' },
    { icon: <SiMessenger size={15} />, name: 'Messenger' },
    { icon: <MdOutlineEmail size={17} />, name: 'Email' },
  ]
  return (
    <div className="w-full aspect-[438/346] rounded-2xl bg-[#F7F7F5] border border-gray-200 p-4 sm:p-5 flex flex-col justify-between overflow-hidden">
      <div className="grid grid-cols-2 gap-2.5">
        {channels.map((c) => (
          <div
            key={c.name}
            className="bg-white border border-gray-200 rounded-xl px-3 py-2.5 flex items-center gap-2 shadow-[0_1px_4px_rgba(0,0,0,0.04)]"
          >
            <span aria-hidden="true" className="text-[#16A34A]">{c.icon}</span>
            <span className="text-[11px] font-medium text-gray-800">{c.name}</span>
            <span className="ml-auto w-1.5 h-1.5 rounded-full bg-[#1ed94f]" />
          </div>
        ))}
      </div>
      <div className="bg-gray-900 rounded-xl px-3.5 py-3 flex items-center gap-2.5">
        <span className="w-2 h-2 rounded-full bg-[#1ed94f] animate-pulse" />
        <div>
          <div className="text-[11px] font-semibold text-white">{copy.storeSyncLive}</div>
          <div className="text-[10px] text-gray-300">{copy.productsOrdersShort}</div>
        </div>
      </div>
    </div>
  )
}

/* ── Mockup: Animasyonlu mağaza entegrasyonları ── */
function StoreIntegrationCard() {
  const copy = useLandingCopy()
  const [activeStore, setActiveStore] = useState(0)
  const stores = [
    { name: 'ikas', detail: copy.ikasDetail, icon: <Zap size={16} /> },
    { name: 'Shopify', detail: copy.shopifyDetail, icon: <SiShopify size={16} /> },
    { name: 'WooCommerce', detail: copy.wooDetail, icon: <SiWoocommerce size={18} /> },
    { name: copy.customStore, detail: copy.customDetail, icon: <Globe2 size={16} /> },
  ]

  useEffect(() => {
    const id = setInterval(() => setActiveStore((store) => (store + 1) % stores.length), 1400)
    return () => clearInterval(id)
  }, [stores.length])

  return (
    <div className="w-full h-full bg-[#F0F7F1] p-4 sm:p-6 flex flex-col justify-center">
      <div className="bg-white rounded-xl border border-gray-200 shadow-[0_4px_20px_rgba(0,0,0,0.06)] p-3 sm:p-4 overflow-hidden">
        <div className="flex items-center justify-between mb-3">
          <div>
            <span className="block text-[11px] font-semibold text-gray-900">{copy.storeHub}</span>
            <span className="block text-[8px] text-gray-600 mt-0.5">{copy.storeHubDesc}</span>
          </div>
          <span className="text-[9px] font-medium px-2 py-0.5 rounded-full bg-green-50 text-[#15803D] border border-green-200 flex items-center gap-1">
            <span className="w-1.5 h-1.5 rounded-full bg-[#16A34A] animate-pulse" /> {copy.syncing}
          </span>
        </div>

        <div className="relative grid grid-cols-2 gap-2">
          <div className="absolute left-1/2 top-1/2 h-[70%] w-px -translate-x-1/2 -translate-y-1/2 bg-gradient-to-b from-transparent via-green-200 to-transparent" />
          <div className="absolute left-1/2 top-1/2 h-px w-[76%] -translate-x-1/2 -translate-y-1/2 bg-gradient-to-r from-transparent via-green-200 to-transparent" />
          {stores.map((store, index) => {
            const active = index === activeStore
            return (
              <div
                key={store.name}
                className={`relative z-10 flex items-center gap-2 rounded-lg border px-2.5 py-2 transition-all duration-500 ${
                  active
                    ? 'scale-[1.02] border-[#72E55D] bg-green-50 shadow-[0_4px_14px_rgba(22,163,74,0.12)]'
                    : 'border-gray-200 bg-white'
                }`}
              >
                <span aria-hidden="true" className={`flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-lg transition-colors duration-500 ${active ? 'bg-[#15803D] text-white' : 'bg-gray-50 text-gray-500'}`}>
                  {store.icon}
                </span>
                <div className="min-w-0">
                  <div className="truncate text-[9px] font-semibold text-gray-900">{store.name}</div>
                  <div className="truncate text-[7px] text-gray-600">{store.detail}</div>
                </div>
                <span className={`ml-auto h-1.5 w-1.5 flex-shrink-0 rounded-full transition-all duration-500 ${active ? 'bg-[#16A34A] shadow-[0_0_0_4px_rgba(22,163,74,0.12)]' : 'bg-gray-200'}`} />
              </div>
            )
          })}
        </div>

        <div className="mt-3 flex items-center gap-2.5 rounded-xl bg-gray-900 px-3 py-2.5 text-white">
          <span className="flex h-7 w-7 items-center justify-center rounded-lg bg-[#72E55D] text-gray-900">
            <Zap size={14} className="animate-pulse" />
          </span>
          <div className="min-w-0 flex-1">
            <div className="flex items-center justify-between gap-2">
              <span className="truncate text-[9px] font-semibold">{stores[activeStore].name} {copy.synced}</span>
              <span className="text-[7px] font-medium text-[#9BE8AD]">{copy.live}</span>
            </div>
            <div className="mt-1.5 h-1 overflow-hidden rounded-full bg-white/10">
              <div key={activeStore} className="h-full rounded-full bg-[#72E55D] animate-[store-sync_1.4s_ease-in-out]" />
            </div>
            <div className="mt-1 text-[7px] text-white/70">1,284 {copy.productsOrders}</div>
          </div>
        </div>
      </div>
    </div>
  )
}

/* ── Mockup: AI Agent sohbeti (kare kart) ── */
function AgentChatCard() {
  const copy = useLandingCopy()
  const [chatStep, setChatStep] = useState(0)

  useEffect(() => {
    const id = setInterval(() => setChatStep((step) => (step >= 4 ? 0 : step + 1)), 1300)
    return () => clearInterval(id)
  }, [])

  const reveal = (step: number) =>
    `transition-all duration-500 ${chatStep >= step ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-2'}`

  return (
    <div className="w-full h-full bg-[#EFF3EE] p-5 sm:p-8 flex items-center justify-center">
      <div className="bg-white rounded-2xl border border-gray-200 shadow-[0_8px_32px_rgba(0,0,0,0.08)] w-full max-w-[340px] overflow-hidden">
        <div className="bg-gray-900 px-4 py-3 flex items-center gap-2.5">
          <div className="w-7 h-7 rounded-full bg-gradient-to-br from-[#f2f047] to-[#1ed94f] flex items-center justify-center text-gray-900 text-[9px] font-bold">
            dk
          </div>
          <div>
            <div className="text-[11px] font-semibold text-white">Nova Store</div>
            <div className="text-[9px] text-[#9be8ad] flex items-center gap-1">
              <span className="w-1.5 h-1.5 rounded-full bg-[#1ed94f]" /> {copy.agentActive}
            </div>
          </div>
          <span className="ml-auto text-[9px] font-medium bg-white/10 text-white px-2 py-0.5 rounded-full">WhatsApp</span>
        </div>
        <div className="p-3.5 flex flex-col gap-2 min-h-[176px]">
          <div className={`${reveal(0)} bg-gray-100 rounded-lg rounded-bl-sm px-3 py-1.5 text-[11px] text-gray-700 self-start max-w-[85%]`}>
            {copy.customerCart}
          </div>
          <div className={`self-end flex items-center gap-1 bg-green-50 rounded-full text-[9px] text-[#15803D] transition-all duration-300 overflow-hidden ${chatStep === 1 ? 'opacity-100 px-2.5 py-1 border border-green-100' : 'opacity-0 h-0 px-2.5 py-0 border-0'}`}>
            <span className="w-1 h-1 rounded-full bg-[#16A34A] animate-bounce" />
            <span className="w-1 h-1 rounded-full bg-[#16A34A] animate-bounce [animation-delay:120ms]" />
            <span className="w-1 h-1 rounded-full bg-[#16A34A] animate-bounce [animation-delay:240ms]" />
            {copy.aiTyping}
          </div>
          <div className={`${reveal(2)} bg-green-50 border border-green-100 rounded-lg rounded-br-sm px-3 py-1.5 text-[11px] text-gray-800 self-end max-w-[85%]`}>
            <CheckCircle2 size={11} className="inline text-[#16A34A] -mt-0.5" /> {copy.agentAnswer}
          </div>
          <div className={`${reveal(3)} flex items-center gap-2 bg-white border border-gray-200 rounded-lg px-2.5 py-1.5`}>
            <div className="w-7 h-7 rounded-md bg-gray-900 flex items-center justify-center text-white">
              <ShoppingCart size={12} />
            </div>
            <div className="flex-1">
              <div className="text-[10px] font-semibold text-gray-900">Core Runner — Black</div>
              <div className="text-[9px] font-bold text-[#15803D]">₺1,890</div>
            </div>
            <span className="text-[8px] font-bold text-[#15803D] bg-green-50 px-1.5 py-0.5 rounded-full border border-green-200">
              {copy.inStock}
            </span>
          </div>
          <div className={`${reveal(4)} text-[9px] text-gray-600 flex items-center gap-1.5 px-1`}>
            <Sparkles size={9} className="text-[#16A34A]" /> {copy.answered}
          </div>
        </div>
      </div>
    </div>
  )
}

/* ─────────────────────────── SECTION 2: PLATFORM ─────────────────────────── */
function Platform() {
  const copy = useLandingCopy()
  return (
    <section id="platform" className="scroll-mt-24 bg-white pt-16 sm:pt-20 lg:pt-32 pb-12 sm:pb-16 lg:pb-24 overflow-hidden">
      <div className="max-w-[1440px] mx-auto">
        <div className="px-5 sm:px-8 lg:px-12 flex items-center gap-3 mb-6 sm:mb-8">
          <span className="w-6 h-6 sm:w-7 sm:h-7 rounded-full bg-gray-900 text-white text-[11px] sm:text-[12px] font-semibold flex items-center justify-center">
            1
          </span>
          <span className="text-[12px] sm:text-[13px] font-medium text-gray-900 border border-gray-200 rounded-full px-3 sm:px-4 py-1 sm:py-1.5">
            {copy.platformBadge}
          </span>
        </div>

        <h2 className="px-5 sm:px-8 lg:px-12 text-[clamp(1.5rem,4vw,3.2rem)] font-medium leading-[1.12] tracking-[-0.02em] text-gray-900 mb-12 sm:mb-16 lg:mb-28">
          {copy.platformTitle1}
          <br className="hidden sm:block" />
          <span className="sm:hidden"> </span>
          {copy.platformTitle2}
        </h2>

        {/* Mobile / tablet */}
        <div className="lg:hidden px-5 sm:px-8 flex flex-col gap-8">
          <p className="text-[15px] sm:text-[17px] leading-[1.6] font-medium text-gray-900">
            {copy.platformDesc}
          </p>
          <button className="group inline-flex items-center gap-4 bg-[#15803D] hover:bg-[#16A34A] text-white text-[13px] sm:text-[14px] font-medium rounded-full pl-5 sm:pl-6 pr-2 py-2 transition-colors self-start">
            <TextRoll text={copy.explorePlatform} />
            <span className="w-7 h-7 sm:w-8 sm:h-8 bg-white rounded-full flex items-center justify-center">
              <ArrowRight
                size={14}
                className={`text-[#16A34A] transition-transform duration-500 ${EASE} group-hover:-rotate-45`}
              />
            </span>
          </button>
          <div className="flex flex-col sm:flex-row gap-4 sm:gap-5">
            <div className="sm:w-[45%]">
              <ChannelsCard />
            </div>
            <div className="sm:w-[55%]">
              <InboxCard />
            </div>
          </div>
        </div>

        {/* Desktop */}
        <div className="hidden lg:grid grid-cols-[26%_1fr_48%] items-end gap-6 xl:gap-8 px-12">
          <div className="self-end">
            <ChannelsCard />
          </div>
          <div className="self-start flex justify-end">
            <div className="flex flex-col gap-8">
              <p className="max-w-[360px] text-[16px] xl:text-[18px] leading-[1.65] font-medium text-gray-900">
                {copy.platformDesc}
              </p>
              <button className="group inline-flex items-center gap-4 bg-[#15803D] hover:bg-[#16A34A] text-white text-[14px] font-medium rounded-full pl-6 pr-2 py-2 transition-colors self-start">
                <TextRoll text={copy.explorePlatform} />
                <span className="w-8 h-8 bg-white rounded-full flex items-center justify-center">
                  <ArrowRight
                    size={14}
                    className={`text-[#16A34A] transition-transform duration-500 ${EASE} group-hover:-rotate-45`}
                  />
                </span>
              </button>
            </div>
          </div>
          <div className="self-end">
            <InboxCard />
          </div>
        </div>
      </div>
    </section>
  )
}

/* ─────────────────────────── SECTION 3: CAPABILITIES ─────────────────────────── */
function Capabilities() {
  const copy = useLandingCopy()
  const moreCapabilities = [
    { icon: <MessageCircle size={16} />, title: copy.capProactiveTitle, desc: copy.capProactiveDesc },
    { icon: <Package size={16} />, title: copy.capOrderTrackingTitle, desc: copy.capOrderTrackingDesc },
    { icon: <Percent size={16} />, title: copy.capDiscountTitle, desc: copy.capDiscountDesc },
    { icon: <RotateCcw size={16} />, title: copy.capRefundTitle, desc: copy.capRefundDesc },
  ]
  return (
    <section id="capabilities" className="scroll-mt-24 bg-[#F5F5F5] pt-16 sm:pt-20 lg:pt-28 pb-16 sm:pb-20 lg:pb-28">
      <div className="max-w-[1440px] mx-auto">
        <div className="px-5 sm:px-8 lg:px-12 flex items-center gap-3 mb-6 sm:mb-8">
          <span className="w-6 h-6 sm:w-7 sm:h-7 rounded-full bg-gray-900 text-white text-[11px] sm:text-[12px] font-semibold flex items-center justify-center">
            2
          </span>
          <span className="text-[12px] sm:text-[13px] font-medium text-gray-900 border border-gray-300 rounded-full px-3 sm:px-4 py-1 sm:py-1.5">
            {copy.agentsBadge}
          </span>
        </div>

        <h2 className="px-5 sm:px-8 lg:px-12 text-[clamp(1.75rem,7vw,4.2rem)] sm:text-[clamp(2.5rem,5vw,4.2rem)] font-medium leading-[1.08] tracking-[-0.03em] text-gray-900 mb-10 sm:mb-14 lg:mb-16">
          {copy.agentsTitle}
        </h2>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-5 sm:gap-6 lg:gap-7 px-5 sm:px-8 lg:px-12">
          {/* Card 1 — Store Integrations */}
          <div id="integrations" className="scroll-mt-24">
            <div className="group relative aspect-[329/246] rounded-2xl overflow-hidden bg-[#F0F7F1] cursor-pointer">
              <StoreIntegrationCard />
              <button className="absolute bottom-4 left-4 h-9 w-9 group-hover:w-[168px] bg-white shadow-[0_2px_8px_rgba(0,0,0,0.1)] rounded-full overflow-hidden transition-all duration-300 ease-in-out flex items-center justify-end pr-[11px]">
                <span className="absolute left-4 text-[13px] font-medium text-gray-900 whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity duration-300 delay-100">
                  {copy.viewIntegrations}
                </span>
                <Link2 size={14} className="text-gray-900 -rotate-45 group-hover:rotate-0 transition-transform duration-300 ease-in-out flex-shrink-0" />
              </button>
            </div>
            <p className="text-[13px] sm:text-[14px] text-gray-600 mt-4 leading-relaxed">
              {copy.integrationsDesc}
            </p>
            <h3 className="text-[14px] sm:text-[15px] font-semibold text-gray-900 mt-1">{copy.integrationsTitle}</h3>
          </div>

          {/* Card 2 — AI Sales Agent */}
          <div>
            <div className="group relative aspect-square rounded-2xl overflow-hidden bg-[#EFF3EE] cursor-pointer">
              <AgentChatCard />
              <button className="absolute bottom-4 left-4 h-9 w-9 group-hover:w-[168px] bg-gray-900 rounded-full overflow-hidden transition-all duration-300 ease-in-out flex items-center justify-end pr-[11px]">
                <span className="absolute left-4 text-[13px] font-medium text-white whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity duration-300 delay-100">
                  {copy.viewAgents}
                </span>
                <ArrowRight
                  size={14}
                  className="text-white -rotate-45 group-hover:rotate-0 transition-transform duration-300 ease-in-out flex-shrink-0"
                />
              </button>
            </div>
            <p className="text-[13px] sm:text-[14px] text-gray-600 mt-4 leading-relaxed">
              {copy.salesDesc}
            </p>
            <h3 className="text-[14px] sm:text-[15px] font-semibold text-gray-900 mt-1">{copy.salesTitle}</h3>
          </div>
        </div>

        {/* Additional capability cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-5 px-5 sm:px-8 lg:px-12 mt-6 sm:mt-7">
          {moreCapabilities.map((item) => (
            <div key={item.title} className="rounded-2xl border border-gray-200 bg-white p-5 sm:p-6">
              <span aria-hidden="true" className="inline-flex h-9 w-9 items-center justify-center rounded-full bg-green-50 text-[#16A34A]">
                {item.icon}
              </span>
              <h3 className="text-[14px] sm:text-[15px] font-semibold text-gray-900 mt-4">{item.title}</h3>
              <p className="text-[13px] text-gray-600 mt-1.5 leading-relaxed">{item.desc}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  )
}

/* ─────────────────────────── SECTION 4: CHANNELS SHOWCASE ─────────────────────────── */
function Channels() {
  const copy = useLandingCopy()
  const channels = [
    { icon: <SiWhatsapp size={18} />, name: 'WhatsApp', desc: copy.channelWhatsappDesc },
    { icon: <SiInstagram size={17} />, name: 'Instagram', desc: copy.channelInstagramDesc },
    { icon: <Send size={18} />, name: 'Telegram', desc: copy.channelTelegramDesc },
    { icon: <MdOutlineChatBubbleOutline size={19} />, name: 'Web Chat', desc: copy.channelWebchatDesc },
    { icon: <MdOutlineEmail size={19} />, name: 'Email', desc: copy.channelEmailDesc },
    { icon: <SiMessenger size={17} />, name: 'Messenger', desc: copy.channelMessengerDesc },
  ]
  return (
    <section id="channels" className="scroll-mt-24 bg-white pt-16 sm:pt-20 lg:pt-28 pb-16 sm:pb-20 lg:pb-28">
      <div className="max-w-[1440px] mx-auto">
        <div className="px-5 sm:px-8 lg:px-12 flex items-center gap-3 mb-6 sm:mb-8">
          <span className="w-6 h-6 sm:w-7 sm:h-7 rounded-full bg-gray-900 text-white text-[11px] sm:text-[12px] font-semibold flex items-center justify-center">
            3
          </span>
          <span className="text-[12px] sm:text-[13px] font-medium text-gray-900 border border-gray-200 rounded-full px-3 sm:px-4 py-1 sm:py-1.5">
            {copy.channelsShowcaseBadge}
          </span>
        </div>

        <h2 className="px-5 sm:px-8 lg:px-12 text-[clamp(1.5rem,4vw,3.2rem)] font-medium leading-[1.12] tracking-[-0.02em] text-gray-900 mb-10 sm:mb-14 lg:mb-16">
          {copy.channelsShowcaseTitle1}
          <br className="hidden sm:block" />
          <span className="sm:hidden"> </span>
          {copy.channelsShowcaseTitle2}
        </h2>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 sm:gap-6 px-5 sm:px-8 lg:px-12">
          {channels.map((channel) => (
            <div key={channel.name} className="rounded-2xl border border-gray-200 bg-[#F7F7F5] p-5 sm:p-6">
              <span aria-hidden="true" className="inline-flex h-9 w-9 items-center justify-center rounded-full bg-white border border-gray-200 text-[#16A34A]">
                {channel.icon}
              </span>
              <h3 className="text-[14px] sm:text-[15px] font-semibold text-gray-900 mt-4">{channel.name}</h3>
              <p className="text-[13px] text-gray-600 mt-1.5 leading-relaxed">{channel.desc}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  )
}

/* ── Mockup: Eğitim/düzeltme demosu ── */
function TrainDemoCard() {
  const copy = useLandingCopy()
  const [corrected, setCorrected] = useState(false)
  const sources = [copy.demoSourceProducts, copy.demoSourcePolicies, copy.demoSourceFaq]

  return (
    <div className="w-full rounded-2xl bg-[#F7F7F5] border border-gray-200 p-4 sm:p-5">
      <div className="bg-white rounded-xl border border-gray-200 shadow-[0_2px_12px_rgba(0,0,0,0.05)] p-4 flex flex-col text-left">
        <div className="flex items-center gap-1.5 pb-3 border-b border-gray-100">
          {sources.map((source, index) => (
            <span
              key={source}
              className={`text-[10px] font-medium px-2.5 py-1 rounded-full ${
                index === 0 ? 'bg-[#15803D] text-white' : 'bg-gray-50 text-gray-500 border border-gray-200'
              }`}
            >
              {source}
            </span>
          ))}
        </div>
        <div className="flex flex-col gap-2 pt-3">
          <div className="bg-gray-100 rounded-lg rounded-bl-sm px-3 py-1.5 text-[11px] text-gray-700 self-start max-w-[85%]">
            {copy.demoQuestion}
          </div>
          <div className="bg-green-50 border border-green-100 rounded-lg rounded-br-sm px-3 py-1.5 text-[11px] text-gray-800 self-end max-w-[85%]">
            {copy.demoAnswer}
          </div>
          {!corrected ? (
            <button
              type="button"
              onClick={() => setCorrected(true)}
              className="mt-1.5 self-start flex items-center gap-1.5 text-[10px] font-medium text-gray-500 border border-gray-200 rounded-full px-2.5 py-1 hover:border-gray-300 hover:text-gray-700 transition"
            >
              <PenLine size={11} /> {copy.demoAddCorrection}
            </button>
          ) : (
            <span className="mt-1.5 self-start flex items-center gap-1.5 text-[10px] font-medium text-[#15803D]">
              <CheckCircle2 size={11} /> {copy.demoCorrectionAdded}
            </span>
          )}
        </div>
      </div>
    </div>
  )
}

/* ─────────────────────────── SECTION 5: HOW IT WORKS ─────────────────────────── */
function HowItWorks() {
  const copy = useLandingCopy()
  const steps = [
    { icon: <Sparkles size={16} />, title: copy.step1Title, desc: copy.step1Desc },
    { icon: <MessageCircle size={16} />, title: copy.step2Title, desc: copy.step2Desc },
    { icon: <Zap size={16} />, title: copy.step3Title, desc: copy.step3Desc },
  ]
  return (
    <section id="how-it-works" className="scroll-mt-24 bg-[#F5F5F5] pt-16 sm:pt-20 lg:pt-28 pb-16 sm:pb-20 lg:pb-28">
      <div className="max-w-[1440px] mx-auto">
        <div className="px-5 sm:px-8 lg:px-12 flex items-center gap-3 mb-6 sm:mb-8">
          <span className="w-6 h-6 sm:w-7 sm:h-7 rounded-full bg-gray-900 text-white text-[11px] sm:text-[12px] font-semibold flex items-center justify-center">
            4
          </span>
          <span className="text-[12px] sm:text-[13px] font-medium text-gray-900 border border-gray-300 rounded-full px-3 sm:px-4 py-1 sm:py-1.5">
            {copy.howItWorksBadge}
          </span>
        </div>

        <h2 className="px-5 sm:px-8 lg:px-12 text-[clamp(1.5rem,4vw,3.2rem)] font-medium leading-[1.12] tracking-[-0.02em] text-gray-900 mb-12 sm:mb-16">
          {copy.howItWorksTitle1}
          <br className="hidden sm:block" />
          <span className="sm:hidden"> </span>
          {copy.howItWorksTitle2}
        </h2>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16 px-5 sm:px-8 lg:px-12 items-center">
          <TrainDemoCard />
          <div className="flex flex-col gap-8 sm:gap-10">
            {steps.map((step, index) => (
              <div key={step.title} className="flex gap-4">
                <div className="flex flex-col items-center">
                  <span className="w-9 h-9 rounded-full bg-white border border-gray-200 text-[#16A34A] flex items-center justify-center flex-shrink-0">
                    {step.icon}
                  </span>
                  {index < steps.length - 1 && <span className="w-px flex-1 bg-gray-200 mt-2" />}
                </div>
                <div className="pb-2">
                  <h3 className="text-[15px] font-semibold text-gray-900">{step.title}</h3>
                  <p className="text-[13px] text-gray-600 mt-1.5 leading-relaxed max-w-sm">{step.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  )
}

function DemoForm() {
  const copy = useLandingCopy()
  const { language } = usePublicI18n()
  const [kvkkAccepted, setKvkkAccepted] = useState(false)
  const [submitted, setSubmitted] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const [submitError, setSubmitError] = useState('')

  const fieldClass =
    'w-full rounded-xl border border-white/15 bg-white/10 px-4 py-3 text-[13px] text-white placeholder:text-white/40 outline-none transition focus:border-[#72E55D] focus:bg-white/[0.13]'

  return (
    <section id="demo-form" className="mb-16 sm:mb-24 scroll-mt-6">
      <div className="overflow-hidden rounded-[28px] bg-gray-900 text-white shadow-[0_24px_70px_rgba(17,24,39,0.18)]">
        <div className="grid lg:grid-cols-[0.85fr_1.15fr]">
          <div className="relative p-7 sm:p-10 lg:p-12 bg-gradient-to-br from-[#173021] via-gray-900 to-gray-900">
            <span className="inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/5 px-3 py-1.5 text-[11px] font-medium text-white/75">
              <span className="w-1.5 h-1.5 rounded-full bg-[#72E55D] animate-pulse" /> {copy.demoBadge}
            </span>
            <h3 className="mt-7 text-[clamp(1.8rem,4vw,3.4rem)] font-medium leading-[1.05] tracking-[-0.03em]">
              {copy.demoTitle}
            </h3>
            <p className="mt-5 max-w-md text-[13px] sm:text-[15px] leading-relaxed text-white/60">
              {copy.demoDesc}
            </p>
            <div className="mt-8 flex flex-wrap gap-2">
              {[copy.demoChip1, copy.demoChip2, copy.demoChip3].map((item) => (
                <span key={item} className="rounded-full border border-white/10 px-3 py-1.5 text-[10px] text-white/70">{item}</span>
              ))}
            </div>
          </div>

          <form
            className="p-7 sm:p-10 lg:p-12"
            onSubmit={async (event) => {
              event.preventDefault()
              if (!kvkkAccepted || submitting) return

              const form = event.currentTarget
              const data = new FormData(form)
              const nameParts = String(data.get('fullName') ?? '').trim().split(/\s+/).filter(Boolean)
              const firstName = nameParts.shift() ?? ''

              setSubmitting(true)
              setSubmitted(false)
              setSubmitError('')
              try {
                await demoRequestsApi.create({
                  firstName,
                  lastName: nameParts.join(' ') || '-',
                  phone: String(data.get('phone') ?? ''),
                  email: String(data.get('email') ?? ''),
                  businessName: String(data.get('company') ?? ''),
                  note: String(data.get('description') ?? ''),
                  source: 'landing',
                  locale: language,
                })
                form.reset()
                setKvkkAccepted(false)
                setSubmitted(true)
              } catch {
                setSubmitError(copy.submitError)
              } finally {
                setSubmitting(false)
              }
            }}
          >
            <div className="grid sm:grid-cols-2 gap-4">
              <label className="text-[11px] font-medium text-white/70">
                {copy.fullName}
                <input required name="fullName" autoComplete="name" placeholder={copy.fullNamePh} className={`${fieldClass} mt-2`} />
              </label>
              <label className="text-[11px] font-medium text-white/70">
                {copy.phone}
                <input required name="phone" type="tel" autoComplete="tel" placeholder="+90 5xx xxx xx xx" className={`${fieldClass} mt-2`} />
              </label>
              <label className="text-[11px] font-medium text-white/70">
                {copy.email}
                <input required name="email" type="email" autoComplete="email" placeholder="isim@firma.com" className={`${fieldClass} mt-2`} />
              </label>
              <label className="text-[11px] font-medium text-white/70">
                {copy.company}
                <input required name="company" autoComplete="organization" placeholder={copy.companyPh} className={`${fieldClass} mt-2`} />
              </label>
            </div>
            <label className="mt-4 block text-[11px] font-medium text-white/70">
              {copy.description}
              <textarea required name="description" rows={4} placeholder={copy.descriptionPh} className={`${fieldClass} mt-2 resize-none`} />
            </label>

            <label className="mt-5 flex items-start gap-3 cursor-pointer group">
              <input
                required
                type="checkbox"
                checked={kvkkAccepted}
                onChange={(event) => {
                  setKvkkAccepted(event.target.checked)
                  setSubmitted(false)
                  setSubmitError('')
                }}
                className="peer sr-only"
              />
              <span className="mt-0.5 w-5 h-5 flex-shrink-0 rounded-md border border-white/25 bg-white/10 peer-checked:border-[#72E55D] peer-checked:bg-[#72E55D] flex items-center justify-center transition">
                {kvkkAccepted && <CheckCircle2 size={13} className="text-gray-900" />}
              </span>
              <span className="text-[11px] leading-relaxed text-white/55 group-hover:text-white/70 transition-colors">
                {copy.privacy}
              </span>
            </label>

            <div className="mt-6 flex flex-col sm:flex-row sm:items-center gap-4">
              <button
                type="submit"
                disabled={!kvkkAccepted || submitting}
                className="group inline-flex items-center justify-between gap-5 rounded-full bg-[#72E55D] px-5 py-2 text-[13px] font-semibold text-gray-900 transition hover:bg-[#8AEE76] disabled:cursor-not-allowed disabled:bg-white/10 disabled:text-white/35"
              >
                {submitting ? copy.submitting : copy.submit}
                <span className="w-8 h-8 rounded-full bg-white flex items-center justify-center">
                  <ArrowRight size={14} className="transition-transform duration-300 group-hover:-rotate-45" />
                </span>
              </button>
              <span aria-live="polite" className={`text-[11px] transition-opacity ${submitError ? 'text-red-300' : 'text-[#9BE8AD]'} ${submitted || submitError ? 'opacity-100' : 'opacity-0'}`}>
                {submitError || copy.submitSuccess}
              </span>
            </div>
          </form>
        </div>
      </div>
    </section>
  )
}

function Faq() {
  const copy = useLandingCopy()
  const items = [
    [copy.faqQuestion1, copy.faqAnswer1],
    [copy.faqQuestion2, copy.faqAnswer2],
    [copy.faqQuestion3, copy.faqAnswer3],
  ]

  return (
    <section id="faq" className="scroll-mt-24 bg-white px-5 py-16 sm:px-8 sm:py-20 lg:px-12 lg:py-28">
      <div className="mx-auto grid max-w-[1344px] gap-10 lg:grid-cols-[0.8fr_1.2fr] lg:gap-20">
        <div>
          <span className="inline-flex rounded-full border border-gray-200 px-3 py-1.5 text-[11px] font-medium text-gray-600">{copy.navFaq}</span>
          <h2 className="mt-6 text-[clamp(2rem,4vw,3.6rem)] font-medium leading-[1.05] tracking-[-0.04em] text-gray-900">{copy.faqTitle}</h2>
        </div>
        <div className="divide-y divide-gray-200 border-y border-gray-200">
          {items.map(([question, answer]) => (
            <details key={question} className="group py-5">
              <summary className="flex cursor-pointer list-none items-center justify-between gap-5 text-[15px] font-semibold text-gray-900">
                {question}
                <ChevronDown size={18} className="shrink-0 text-gray-400 transition-transform group-open:rotate-180" />
              </summary>
              <p className="max-w-2xl pt-4 text-[13px] leading-6 text-gray-600">{answer}</p>
            </details>
          ))}
        </div>
      </div>
    </section>
  )
}

/* ─────────────────────────── FOOTER ─────────────────────────── */
function Footer() {
  const copy = useLandingCopy()
  return (
    <footer className="bg-white border-t border-gray-100 pt-16 sm:pt-20 lg:pt-28 pb-8">
      <div className="max-w-[1440px] mx-auto px-5 sm:px-8 lg:px-12">
        <div className="text-center mb-16 sm:mb-20">
          <h2 className="text-[clamp(1.5rem,4vw,3.2rem)] font-medium leading-[1.12] tracking-[-0.02em] text-gray-900 mb-8 max-w-3xl mx-auto">
            {copy.footerTitle1} <span className="text-[#16A34A]">{copy.footerTitleAccent}</span> {copy.footerTitle2}
          </h2>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <button className="group inline-flex items-center gap-4 bg-[#15803D] hover:bg-[#16A34A] text-white text-[14px] font-medium rounded-full pl-6 pr-2 py-2 transition-colors">
              <TextRoll text={copy.startFree} />
              <span className="w-8 h-8 bg-white rounded-full flex items-center justify-center">
                <ArrowRight
                  size={14}
                  className={`text-[#16A34A] transition-transform duration-500 ${EASE} group-hover:-rotate-45`}
                />
              </span>
            </button>
            <a href="#demo-form" className="group inline-flex items-center gap-4 bg-gray-900 text-white text-[14px] font-medium rounded-full pl-6 pr-2 py-2">
              <TextRoll text={copy.bookDemo} />
              <span className="w-8 h-8 bg-white rounded-full flex items-center justify-center">
                <ArrowRight
                  size={14}
                  className={`text-gray-900 transition-transform duration-500 ${EASE} group-hover:-rotate-45`}
                />
              </span>
            </a>
          </div>
        </div>

        <DemoForm />

        <div className="flex flex-col md:flex-row items-center justify-between gap-4 border-t border-gray-100 pt-6 text-[12px] text-gray-500">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 bg-gray-900 rounded-full flex items-center justify-center overflow-hidden">
              <img src={navLogo} alt="dotkesk logo" width="40" height="29" loading="lazy" className="w-full h-full object-contain" />
            </div>
            <span className="font-semibold text-gray-900">dotkesk</span>
          </div>
          <span>
            © 2026 <span className="text-[#15803D] font-medium">dotkesk</span> · AI Commerce OS · WhatsApp, Instagram,
            Messenger, Email &amp; Web Chat
          </span>
          <div className="flex items-center gap-4">
            <a
              href="https://www.linkedin.com/company/dotkesk/"
              target="_blank"
              rel="noopener noreferrer"
              aria-label="LinkedIn"
              className="text-gray-600 hover:text-gray-900 transition-colors"
            >
              <FaLinkedin aria-hidden="true" size={15} />
            </a>
            <a
              href="https://www.instagram.com/dotkesk/"
              target="_blank"
              rel="noopener noreferrer"
              aria-label="Instagram"
              className="text-gray-600 hover:text-gray-900 transition-colors"
            >
              <SiInstagram aria-hidden="true" size={15} />
            </a>
            <a href="mailto:admin@dotkesk.com" className="hover:text-gray-900 transition-colors">
              admin@dotkesk.com
            </a>
            <a href="/blog" className="hover:text-gray-900 transition-colors">
              {copy.blog}
            </a>
            <a href="/docs" className="hover:text-gray-900 transition-colors">
              {copy.docs}
            </a>
            <a href="#demo-form" className="hover:text-gray-900 transition-colors">
              {copy.pricing}
            </a>
            <a href={appLoginUrl} className="hover:text-gray-900 transition-colors">
              {copy.signIn}
            </a>
          </div>
        </div>
      </div>
    </footer>
  )
}

/* ── Landing blog bölümü: son yazılar /blog sayfasına bağlanır ── */
function BlogSection() {
  const copy = useLandingCopy()
  const { language } = usePublicI18n()
  const sectionRef = useRef<HTMLElement>(null)
  const [shouldLoad, setShouldLoad] = useState(false)
  const [posts, setPosts] = useState<Awaited<ReturnType<typeof blogApi.list>>['items']>([])
  const [loadStatus, setLoadStatus] = useState<'idle' | 'loading' | 'loaded' | 'error'>('idle')

  useEffect(() => {
    const section = sectionRef.current
    if (!section || shouldLoad) return
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (!entry.isIntersecting) return
        setShouldLoad(true)
        observer.disconnect()
      },
      { rootMargin: '600px' },
    )
    observer.observe(section)
    return () => observer.disconnect()
  }, [shouldLoad])

  useEffect(() => {
    if (!shouldLoad) return
    let cancelled = false
    const load = async () => {
      setLoadStatus('loading')
      try {
        let result = await blogApi.list({ pageSize: 3, language })
        if (result.total === 0 && language !== 'tr') {
          const fallback = await blogApi.list({ pageSize: 3, language: 'tr' })
          if (fallback.total > 0) result = fallback
        }
        if (!cancelled) {
          setPosts(result.items)
          setLoadStatus('loaded')
        }
      } catch {
        if (!cancelled) setLoadStatus('error')
      }
    }
    void load()
    return () => { cancelled = true }
  }, [language, shouldLoad])

  return (
    <section ref={sectionRef} id="blog" className="bg-white py-20 sm:py-24 lg:py-28">
      <div className="max-w-[1440px] mx-auto px-5 sm:px-8 lg:px-12">
        <div className="mb-12 sm:mb-14">
          <div className="inline-flex items-center gap-2 rounded-full border border-[#D8EADD] bg-[#F1F7F1] px-4 py-1.5 text-[12px] font-semibold text-[#15803D] mb-6">
            {copy.blogBadge}
          </div>
          <h2 className="text-[clamp(1.5rem,4vw,3.2rem)] font-medium leading-[1.12] tracking-[-0.02em] text-gray-900 mb-4 max-w-3xl">
            {copy.blogTitle1} <span className="text-[#16A34A]">{copy.blogTitleAccent}</span> {copy.blogTitle2}
          </h2>
          <p className="text-[15px] leading-relaxed text-gray-500 max-w-2xl">{copy.blogSubtitle}</p>
        </div>

        <div className="grid gap-8 sm:grid-cols-2 lg:grid-cols-3">
          {posts.length === 0 && loadStatus !== 'loaded' && loadStatus !== 'error' && [0, 1, 2].map((item) => (
            <div key={item} aria-hidden="true" className="overflow-hidden rounded-3xl border border-gray-100 bg-white shadow-[0_2px_12px_rgba(15,23,42,0.04)]">
              <div className="aspect-[1200/630] animate-pulse bg-gray-100" />
              <div className="space-y-3 p-6">
                <div className="h-4 w-20 animate-pulse rounded-full bg-green-50" />
                <div className="h-5 w-4/5 animate-pulse rounded bg-gray-100" />
                <div className="h-4 w-full animate-pulse rounded bg-gray-100" />
              </div>
            </div>
          ))}
          {posts.map((post) => {
            const cover = resolveCoverUrl(post.coverImageUrl)
            const locale = languages.find((item) => item.code === language)?.locale ?? 'tr-TR'
            const date = post.publishedAt
              ? new Intl.DateTimeFormat(locale, { day: 'numeric', month: 'long', year: 'numeric' }).format(new Date(post.publishedAt))
              : ''
            return (
              <article key={post.id} className="group flex flex-col overflow-hidden rounded-3xl border border-gray-100 bg-white shadow-[0_2px_12px_rgba(15,23,42,0.04)] transition-all duration-300 hover:-translate-y-1 hover:shadow-[0_18px_50px_rgba(15,23,42,0.10)]">
                <a href={`/blog/${post.slug}`} className="block aspect-[1200/630] overflow-hidden bg-[#0B120E]">
                  {cover ? (
                    <img src={cover} alt={post.title} loading="lazy" className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-[1.03]" />
                  ) : (
                    <div className="flex h-full w-full items-center justify-center bg-gradient-to-br from-[#0B120E] to-[#16A34A]/30 text-[28px] font-semibold text-white/80">dotkesk</div>
                  )}
                </a>
                <div className="flex flex-1 flex-col p-6">
                  {post.tags.length > 0 && (
                    <div className="mb-3 flex flex-wrap gap-1.5">
                      {post.tags.slice(0, 3).map((tag) => (
                        <span key={tag} className="rounded-full bg-[#F1F7F1] px-2.5 py-1 text-[11px] font-medium text-[#15803D]">
                          {tag}
                        </span>
                      ))}
                    </div>
                  )}
                  <h3 className="text-[17px] font-semibold leading-snug tracking-[-0.01em] text-gray-900">
                    <a href={`/blog/${post.slug}`} className="transition-colors group-hover:text-[#15803D]">{post.title}</a>
                  </h3>
                  <p className="mt-2 line-clamp-2 text-[13.5px] leading-relaxed text-gray-500">{post.excerpt}</p>
                  <div className="mt-4 flex items-center gap-4 border-t border-gray-50 pt-4 text-[12px] text-gray-600">
                    {date && <time dateTime={post.publishedAt ?? undefined}>{date}</time>}
                    <span className="inline-flex items-center gap-1"><Clock size={12} /> {post.readingMinutes} {copy.blogMinRead}</span>
                    <span className="inline-flex items-center gap-1"><Eye size={12} /> {post.viewCount} {copy.blogViews}</span>
                  </div>
                </div>
              </article>
            )
          })}
        </div>

        <div className="mt-12 flex justify-center">
          <a href="/blog" className="group inline-flex items-center gap-4 bg-gray-900 hover:bg-gray-800 text-white text-[14px] font-medium rounded-full pl-6 pr-2 py-2 transition-colors">
            <TextRoll text={copy.blogViewAll} />
            <span className="w-8 h-8 bg-[#16A34A] rounded-full flex items-center justify-center">
              <ArrowRight size={14} className={`text-white transition-transform duration-500 ${EASE} group-hover:-rotate-45`} />
            </span>
          </a>
        </div>
      </div>
    </section>
  )
}

export function LandingPage() {
  useEffect(() => {
    const scriptId = 'dotkesk-landing-webchat'
    if (document.getElementById(scriptId)) return

    let script: HTMLScriptElement | null = null
    let loaded = false
    const loadWebChat = () => {
      if (loaded || document.getElementById(scriptId)) return
      loaded = true
      script = document.createElement('script')
      script.id = scriptId
      script.src = '/webchat-loader.js'
      script.dataset.channelId = landingWebChatChannelId
      script.dataset.apiBase = landingWebChatApiBase
      script.dataset.launcherPrompt = 'Merhaba! Dotkesk hakkında ne öğrenmek istersiniz?'
      document.body.appendChild(script)
    }

    // Webchat ilk boyamayı etkilemesin; ziyaretçinin ilk etkileşiminde yüklenir.
    const events = ['pointerdown', 'keydown', 'touchstart'] as const
    events.forEach((event) => window.addEventListener(event, loadWebChat, { once: true, passive: true }))

    return () => {
      events.forEach((event) => window.removeEventListener(event, loadWebChat))
      script?.remove()
      document
        .querySelectorAll(`iframe[data-dotkesk-webchat-channel="${landingWebChatChannelId}"]`)
        .forEach((frame) => frame.remove())
      document
        .querySelectorAll(`[data-dotkesk-webchat-nudge-channel="${landingWebChatChannelId}"]`)
        .forEach((nudge) => nudge.remove())
      document
        .querySelectorAll(`[data-dotkesk-webchat-nudge-style-channel="${landingWebChatChannelId}"]`)
        .forEach((style) => style.remove())
    }
  }, [])

  return (
    <main className="scroll-smooth">
      <Hero />
      <TrustBar />
      <Platform />
      <Capabilities />
      <Channels />
      <HowItWorks />
      <BlogSection />
      <Faq />
      <Footer />
    </main>
  )
}
